import React, { useEffect, useMemo, useRef, useState } from "react"
import * as frameModalStyles from "./frameModal.module.scss"
import { GatsbyImage } from "gatsby-plugin-image"
import { GrFacebook, GrInstagram } from "react-icons/gr"
import { BiLinkExternal } from "react-icons/bi"
import { IoClose } from "react-icons/io5"
import FramePromoCallout from "./framePromoCallout"

const DESKTOP_MEDIA_QUERY = "(min-width: 851px)"

const FrameModal = ({ isOpen, frameData, onRequestClose }) => {
  const modalPanelRef = useRef(null)
  const closeButtonRef = useRef(null)
  const lastActiveElementRef = useRef(null)

  const isBrowser = typeof window !== "undefined"

  const [isDesktopLayout, setIsDesktopLayout] = useState(() => {
    if (!isBrowser) return false
    return window.matchMedia(DESKTOP_MEDIA_QUERY).matches
  })

  // Keep layout state correct even if the user resizes BEFORE opening the modal.
  useEffect(() => {
    if (!isBrowser) return

    const mediaQueryList = window.matchMedia(DESKTOP_MEDIA_QUERY)

    const handleMediaChange = eventOrList => {
      const nextMatches =
        typeof eventOrList?.matches === "boolean"
          ? eventOrList.matches
          : mediaQueryList.matches

      setIsDesktopLayout(nextMatches)
    }

    // Sync immediately on mount.
    handleMediaChange(mediaQueryList)

    if (typeof mediaQueryList.addEventListener === "function") {
      mediaQueryList.addEventListener("change", handleMediaChange)
      return () =>
        mediaQueryList.removeEventListener("change", handleMediaChange)
    }

    // Safari fallback
    mediaQueryList.addListener(handleMediaChange)
    return () => mediaQueryList.removeListener(handleMediaChange)
  }, [isBrowser])

  useEffect(() => {
    if (!isOpen) return
    if (!isBrowser) return

    // Save the element that opened the modal so we can restore focus on close.
    lastActiveElementRef.current = document.activeElement

    const originalOverflowValue = document.body.style.overflow
    document.body.style.overflow = "hidden"

    const focusFirstElementInModal = () => {
      if (closeButtonRef.current) {
        closeButtonRef.current.focus()
        return
      }
      if (modalPanelRef.current) modalPanelRef.current.focus()
    }

    const getFocusableElements = () => {
      if (!modalPanelRef.current) return []

      const focusableSelector = [
        'a[href]:not([tabindex="-1"])',
        'button:not([disabled]):not([tabindex="-1"])',
        'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
        'select:not([disabled]):not([tabindex="-1"])',
        'textarea:not([disabled]):not([tabindex="-1"])',
        '[contenteditable="true"]:not([tabindex="-1"])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(",")

      return Array.from(
        modalPanelRef.current.querySelectorAll(focusableSelector)
      ).filter(elementNode => {
        const elementIsHidden =
          elementNode.getAttribute("aria-hidden") === "true" ||
          elementNode.hidden

        const elementIsVisible =
          elementNode.offsetWidth > 0 ||
          elementNode.offsetHeight > 0 ||
          elementNode.getClientRects().length > 0

        return !elementIsHidden && elementIsVisible
      })
    }

    const trapTabKey = event => {
      if (event.key !== "Tab") return

      const focusableElements = getFocusableElements()

      if (focusableElements.length === 0) {
        event.preventDefault()
        if (modalPanelRef.current) modalPanelRef.current.focus()
        return
      }

      const firstFocusable = focusableElements[0]
      const lastFocusable = focusableElements[focusableElements.length - 1]
      const activeElementNode = document.activeElement

      if (event.shiftKey) {
        if (
          activeElementNode === firstFocusable ||
          activeElementNode === modalPanelRef.current
        ) {
          event.preventDefault()
          lastFocusable.focus()
        }
        return
      }

      if (activeElementNode === lastFocusable) {
        event.preventDefault()
        firstFocusable.focus()
      }
    }

    const handleKeyDown = event => {
      if (event.key === "Escape") {
        event.preventDefault()
        onRequestClose()
        return
      }

      trapTabKey(event)
    }

    window.addEventListener("keydown", handleKeyDown)

    // Put focus inside the modal immediately.
    focusFirstElementInModal()

    return () => {
      document.body.style.overflow = originalOverflowValue
      window.removeEventListener("keydown", handleKeyDown)

      // Restore focus to the opener.
      const lastActiveElement = lastActiveElementRef.current
      if (lastActiveElement && typeof lastActiveElement.focus === "function") {
        lastActiveElement.focus()
      }
    }
  }, [isOpen, isBrowser, onRequestClose])

  const normalizedFrameData = useMemo(() => {
    if (!frameData) return null

    const {
      title,
      location,
      logo,
      image,
      imageDescription,
      lifestyleImages,
      website,
      facebookLink,
      instagramLink,
      html,
      promo,
    } = frameData

    const cleanedLocationText = (location || "")
      .replace(/^available at\s+/i, "")
      .replace(/^available\s+/i, "")
      .trim()

    const extraImagesArray = Array.isArray(lifestyleImages)
      ? lifestyleImages
      : []

    return {
      title,
      cleanedLocationText,
      logo,
      image,
      imageDescription,
      extraImagesArray,
      website,
      facebookLink,
      instagramLink,
      html,
      promo,
    }
  }, [frameData])

  if (!isOpen || !normalizedFrameData) return null

  const {
    title,
    cleanedLocationText,
    logo,
    image,
    imageDescription,
    extraImagesArray,
    website,
    facebookLink,
    instagramLink,
    html,
    promo,
  } = normalizedFrameData

  // Desktop: show scrollable gallery if there are lifestyle images.
  const galleryImages = [image, ...extraImagesArray].filter(Boolean)
  const shouldShowDesktopGallery = isDesktopLayout && galleryImages.length > 1

  // Mobile: show lifestyle images below bio (only the lifestyle images, not the main hero image).
  const shouldShowMobileLifestyleGallery =
    !isDesktopLayout && extraImagesArray.length > 0

  return (
    <div
      className={frameModalStyles.backdrop}
      role="presentation"
      onMouseDown={event => {
        if (event.target === event.currentTarget) onRequestClose()
      }}
    >
      <div
        className={frameModalStyles.modal}
        role="dialog"
        aria-modal="true"
        aria-label={`${title} details`}
        tabIndex={-1}
        ref={modalPanelRef}
      >
        <div className={frameModalStyles.modalTop}>
          <div className={frameModalStyles.modalHeader}>
            <div className={frameModalStyles.headerTitle}>
              <span className={frameModalStyles.headerTitleText}>{title}</span>
            </div>

            <button
              ref={closeButtonRef}
              type="button"
              className={frameModalStyles.closeButton}
              onClick={onRequestClose}
              aria-label="Close modal"
            >
              <IoClose />
            </button>
          </div>

          <FramePromoCallout
            promo={promo}
            className={frameModalStyles.promoCallout}
          />
        </div>

        <div className={frameModalStyles.contentGrid}>
          {/* LEFT COLUMN */}
          <div className={frameModalStyles.leftColumn}>
            <div className={frameModalStyles.logoWrapper}>
              <GatsbyImage
                className={frameModalStyles.logo}
                image={logo}
                imgStyle={{ objectFit: `contain` }}
                alt={`${title} logo`}
              />
            </div>

            <div
              className={frameModalStyles.locationLine}
              aria-label="Location"
            >
              {cleanedLocationText}
            </div>

            <div
              className={
                shouldShowDesktopGallery
                  ? `${frameModalStyles.imageWrapper} ${frameModalStyles.imageWrapperScrollable}`
                  : frameModalStyles.imageWrapper
              }
            >
              {shouldShowDesktopGallery ? (
                <div
                  className={frameModalStyles.galleryStack}
                  aria-label="Image gallery"
                >
                  {galleryImages.map((galleryImageData, galleryIndex) => {
                    const galleryAltText =
                      galleryIndex === 0
                        ? imageDescription
                        : `Lifestyle image ${galleryIndex} for ${title}`

                    return (
                      <div
                        key={`frame-gallery-${galleryIndex}`}
                        className={frameModalStyles.galleryItem}
                      >
                        <GatsbyImage
                          className={frameModalStyles.galleryImage}
                          image={galleryImageData}
                          imgStyle={{ objectFit: `contain` }}
                          alt={galleryAltText}
                        />
                      </div>
                    )
                  })}
                </div>
              ) : (
                <GatsbyImage
                  className={frameModalStyles.frameImage}
                  image={image}
                  imgStyle={{ objectFit: `contain` }}
                  alt={imageDescription}
                />
              )}
            </div>

            {facebookLink || instagramLink || website ? (
              <div className={frameModalStyles.linksRow} aria-label="Links">
                {facebookLink ? (
                  <a
                    href={facebookLink}
                    target="__blank"
                    rel="noreferrer"
                    aria-label="Facebook"
                  >
                    <GrFacebook />
                    <span className={frameModalStyles.linkText}>Facebook</span>
                  </a>
                ) : null}

                {instagramLink ? (
                  <a
                    href={instagramLink}
                    target="__blank"
                    rel="noreferrer"
                    aria-label="Instagram"
                  >
                    <GrInstagram />
                    <span className={frameModalStyles.linkText}>Instagram</span>
                  </a>
                ) : null}

                {website ? (
                  <a
                    href={website}
                    target="__blank"
                    rel="noreferrer"
                    aria-label="Website"
                  >
                    <BiLinkExternal />
                    <span className={frameModalStyles.linkText}>Website</span>
                  </a>
                ) : null}
              </div>
            ) : null}
          </div>

          {/* RIGHT COLUMN */}
          <div className={frameModalStyles.rightColumn}>
            <div
              className={frameModalStyles.bio}
              dangerouslySetInnerHTML={{ __html: html }}
            />

            {shouldShowMobileLifestyleGallery ? (
              <div
                className={frameModalStyles.mobileLifestyleGallery}
                aria-label="Lifestyle images"
              >
                <div className={frameModalStyles.mobileLifestyleStack}>
                  {extraImagesArray.map(
                    (lifestyleImageData, lifestyleIndex) => (
                      <div
                        key={`frame-mobile-lifestyle-${lifestyleIndex}`}
                        className={frameModalStyles.mobileLifestyleItem}
                      >
                        <GatsbyImage
                          className={frameModalStyles.mobileLifestyleImage}
                          image={lifestyleImageData}
                          imgStyle={{ objectFit: `contain` }}
                          alt={`Lifestyle image ${
                            lifestyleIndex + 1
                          } for ${title}`}
                        />
                      </div>
                    )
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

export default FrameModal
